import { ListItemNode, ListNode } from '@lexical/list';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { $dfs } from '@lexical/utils';
import type { EditorState } from 'lexical';
import { $getRoot } from 'lexical';

import humanizeDuration from 'humanize-duration';
import { DurationTargetDefaults } from 'js/src/client';
import { $isBlockNode } from 'js/src/components/app/doc-to-script/nodes/BlockNode';
import type {
    ReadonlyIdeaOutlineValueSet,
    Script,
    ScriptSegment,
} from 'js/src/data/script-composer/script-composer-types';
import { COOKIE_KEYS, MIN_WORDS_PER_EDITED_SCRIPT_BLOCK } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';

export const DOC_IMPORT_EDITOR_CONFIG = {
    namespace: 'DocImportEditor',
    theme: {
        placeholder: 'editor-placeholder',
        paragraph: 'font-l5 font-l5--small',
    },
    onError: (error: Error) => {
        ErrorReporter.capture(error);
    },
    nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode],
};

import Cookies from 'js-cookie';

export const DEFAULT_IDEA_OUTLINE_VALUES: ReadonlyIdeaOutlineValueSet = {
    subject: '',
    use_case: '',
    references: ['', '', ''],
};

export const MAX_AI_VOICEOVER_WORD_COUNT = 1200;
export const MAX_AI_AVATAR_WORD_COUNT = 500;

export const getMaxWordCount = (isAvatar: boolean) =>
    isAvatar ? MAX_AI_AVATAR_WORD_COUNT : MAX_AI_VOICEOVER_WORD_COUNT;

export const editorHasText = (editorState?: EditorState): boolean => {
    if (!editorState || editorState.isEmpty()) {
        return false;
    }
    let textContent = '';
    editorState.read(() => {
        textContent = $getRoot().getTextContent().trim();
    });
    return !!textContent;
};

export const scriptComposerHumanizeDuration = (duration: number) => {
    const humanizeDurationFormatter = {
        languages: {
            en: {
                m: () => ' min',
                s: () => 's',
            },
        },
        spacer: '',
        delimiter: ' ',
    };
    return humanizeDuration(duration * 1000, humanizeDurationFormatter);
};

export const getAllScriptBlockText = (editorState: EditorState) => {
    let sentences: Array<string> = [];
    editorState.read(() => {
        sentences = $dfs()
            .map(({ node }) => {
                if ($isBlockNode(node)) {
                    const content = node.getTextContent().trim();
                    if (content) {
                        return content;
                    }
                }
            })
            .filter((sentence): sentence is string => sentence !== undefined && sentence !== '');
    });
    return sentences;
};

/**
 * Converts a script represented as an array of sentences with their word count to a condensed script
 * so that each script 'sentence' block contains at least MIN_WORDS_PER_EDITED_SCRIPT_BLOCK number of words.
 * This is mostly so that each block has enough words to provide enough context for a good AI voiceover.
 * breakIndices is used to specify exceptions, where the block should terminate and start a new block whether it reaches the char limit or not.
 */
export const condenseScript = (
    scriptSegments: Array<ScriptSegment>,
    breakIndices: Array<number> = [],
): Script => {
    let currentWordCount = 0;
    let currentText = '';
    const sentences: string[] = [];
    scriptSegments.forEach((segment, index) => {
        currentWordCount += segment.wordCount;
        currentText += segment.text + ' ';
        if (currentWordCount >= MIN_WORDS_PER_EDITED_SCRIPT_BLOCK || breakIndices.includes(index)) {
            sentences.push(currentText.trim());
            currentText = '';
            currentWordCount = 0;
        }
    });

    if (
        currentText &&
        (currentWordCount >= MIN_WORDS_PER_EDITED_SCRIPT_BLOCK || sentences.length === 0)
    ) {
        sentences.push(currentText);
    } else if (currentText && sentences) {
        sentences[sentences.length - 1] += ' ' + currentText;
    }
    return [
        {
            header: '',
            sentences,
        },
    ];
};

export const getLastUsedDuration = () => {
    return (
        parseInt(Cookies.get(COOKIE_KEYS.LAST_SELECTED_SCRIPT_DURATION_TARGET) || '') ||
        DurationTargetDefaults.DEFAULT
    );
};

export const getSentencesFromScript = (script: Script): string[] => {
    const sentences: string[] = [];
    script.forEach((section) => {
        section.sentences.forEach((sentence) => {
            sentences.push(sentence);
        });
    });
    return sentences;
};

// Build a video_prompt instruction that forces the scene-recommendation LLM to use the
// script-composer blocks as hard scene boundaries (one scene per block, in order).
export const buildBlockSegmentationPrompt = (blocks: string[]): string | null => {
    if (blocks.length < 2) return null;
    const numbered = blocks.map((block, i) => `${i + 1}. ${block}`).join('\n');
    return (
        'You MUST segment the script into scenes exactly as follows, ' +
        'producing PRECISELY ONE SCENE per numbered entry, in order, with no merging or splitting. ' +
        'YOU MUST FOLLOW THIS INSTRUCTION:\n' +
        numbered
    );
};
